/**
 * UserSettings Model - Data model for user application settings
 * 
 * MIGRATION STATUS: Uses Pinia (not Vuex)
 * 
 * PURPOSE:
 * Encapsulates user preferences and UI state flags that don't belong in core models
 * Centralizes all settings management to reduce scattered $store.dispatch() calls
 * 
 * ARCHITECTURE:
 * - Extends AbstractPiniaModel for consistency with other migrated models
 * - All data is persisted to localStorage via Pinia store
 * - Provides a clean static API: UserSettings.setTestDuration(value)
 * - Component interaction is simplified: one-line static method instead of this.$store.dispatch()
 * 
 * STORAGE:
 * Settings are stored in Pinia state:
 * - localStorage key: "${userEmail}@pinia"
 * - Nested under: { userSettingsStore: { testDuration, apiAccount, ... } }
 * 
 * USAGE EXAMPLES:
 * // Get settings
 * UserSettings.getTestDuration()
 * UserSettings.getApiAccount()
 * UserSettings.isMarketingTextHidden(2)
 * UserSettings.isRenewalHidden(professionId)
 * 
 * // Set settings
 * UserSettings.setTestDuration(duration)
 * UserSettings.setApiAccount(account)
 * UserSettings.setHideMarketingText(slot)
 */

import AbstractPiniaModel from './AbstractPiniaModel'
import { useUserSettingsStore } from '../pinia/stores/userSettings'

/**
 * UserSettings Model Class
 * 
 * Provides static methods to manage user application settings without direct store access
 * 
 * STATIC METHODS (accessing state via Pinia):
 * - UserSettings.getAllSettings()              → Get all settings
 * - UserSettings.getTestDuration()             → Get test duration
 * - UserSettings.getApiAccount()               → Get API account
 * - UserSettings.setTestDuration(value)        → Set test duration
 * - UserSettings.setHideMarketingText(slot)    → Hide marketing text
 * - UserSettings.setHideRenewal(professionId)  → Hide renewal offer
 */
export class UserSettings extends AbstractPiniaModel {
    /**
     * Create a new UserSettings instance (minimal use)
     * Most interaction is through static methods
     * @param {Object} settingsData - Settings data
     */
    constructor(settingsData) {
        super(settingsData || {})
    }

    // ========== STATIC METHODS ==========
    
    /**
     * Returns the Pinia user settings store
     * This is the single source of truth for settings state
     * Called by AbstractPiniaModel.dispatch() and getters
     * @returns {Store} Pinia user settings store instance
     */
    static piniaStore() {
        return useUserSettingsStore()
    }

    // ========== Setting Getters ==========

    /**
     * Get all user settings
     * @returns {Object} All settings object
     */
    static getAllSettings() {
        return this.piniaStore().settings
    }

    /**
     * Get test duration setting
     * @returns {*} Test duration value or undefined
     */
    static getTestDuration() {
        return this.piniaStore().settings.test_duration
    }

    /**
     * Get API account setting
     * @returns {*} API account value or undefined
     */
    static getApiAccount() {
        return this.piniaStore().settings.api_account
    }

    /**
     * Get last used profession
     * @returns {number|undefined} Profession ID or undefined
     */
    static getLastProfession() {
        return this.piniaStore().settings.last_profession
    }

    /**
     * Get dashboard profession setting
     * @returns {number|undefined} Profession ID or undefined
     */
    static getDashboardProfession() {
        return this.piniaStore().settings.dashboard_profession
    }

    /**
     * Get welcome random value
     * @returns {number|undefined} Random value or undefined
     */
    static getWelcomeRandom() {
        return this.piniaStore().settings.welcome_random
    }

    /**
     * Get welcome quote value
     * @returns {number|undefined} Quote value or undefined
     */
    static getWelcomeQuote() {
        return this.piniaStore().settings.welcome_quote
    }

    /**
     * Check if a setting key exists
     * @param {string} key - Setting key to check
     * @returns {boolean} Whether the key exists
     */
    static hasSetting(key) {
        return key in this.getAllSettings()
    }

    /**
     * Get a custom setting by key
     * @param {string} key - Setting key
     * @returns {*} Setting value or undefined
     */
    static getSetting(key) {
        return this.getAllSettings()[key]
    }

    // ========== Setting Setters ==========

    /**
     * Set test duration
     * Replaces the entire settings object with new data
     * Preserves certain welcome flags during the replacement
     * @param {*} duration - Test duration value
     */
    static setTestDuration(duration) {
        this.dispatch('setTestDuration', duration)
    }

    /**
     * Set API account info
     * @param {*} account - API account value
     */
    static setApiAccount(account) {
        this.dispatch('setApiAccount', account)
    }

    /**
     * Set last used profession
     * Called when user switches to a different profession
     * @param {number} professionId - Profession ID
     */
    static setLastProfession(professionId) {
        this.dispatch('setLastProfession', professionId)
    }

    /**
     * Set dashboard profession
     * @param {number} professionId - Profession ID
     */
    static setDashboardProfession(professionId) {
        this.dispatch('setDashboardProfession', professionId)
    }

    /**
     * Set welcome random value
     * Used to randomize welcome content display
     * @param {number} value - Random value
     */
    static setWelcomeRandom(value) {
        this.dispatch('setWelcomeRandom', value)
    }

    /**
     * Set welcome quote value
     * Used to track which welcome quote was shown
     * @param {number} value - Quote value
     */
    static setWelcomeQuote(value) {
        this.dispatch('setWelcomeQuote', value)
    }

    /**
     * Hide marketing text for a specific slot
     * Called when user closes a marketing text banner
     * @param {number} slot - Slot number (1, 2, 3, etc.)
     */
    static setHideMarketingText(slot) {
        this.dispatch('setHideMarketingText', slot)
    }

    /**
     * Hide renewal offer for a specific profession
     * Called when user dismisses the renewal offer
     * @param {number} professionId - Profession ID
     */
    static setHideRenewal(professionId) {
        this.dispatch('setHideRenewal', professionId)
    }

    /**
     * Check if marketing text is hidden for a slot
     * @param {number} slot - Slot number
     * @returns {boolean} Whether the marketing text is hidden
     */
    static isMarketingTextHidden(slot) {
        return this.piniaStore().isMarketingTextHidden(slot)
    }

    /**
     * Check if renewal offer is hidden for a profession
     * @param {number} professionId - Profession ID
     * @returns {boolean} Whether the renewal offer is hidden
     */
    static isRenewalHidden(professionId) {
        return this.piniaStore().isRenewalHidden(professionId)
    }

    /**
     * Replace all settings
     * Accepts settings object and syncs to Pinia
     * @param {Object} newSettings - New settings object
     */
    static setAllSettings(newSettings) {
        this.dispatch('setAllSettings', newSettings)
    }
}

export default UserSettings
